import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useFormatting } from '@ayeldo/frontend-core/hooks/useFormatting';

import { useIdpSession } from '../../hooks/useIdpSession';
import { adminApi, type GroupDetail, type IdentitySummary } from '../../services/admin-api';
import { APIError } from '../../services/idp-api';

export function GroupDetailPage() {
  const { t } = useTranslation('idp');
  const navigate = useNavigate();
  const { groupId } = useParams<{ groupId: string }>();
  const { formatDate } = useFormatting();
  const { isAuthenticated, isLoading: sessionLoading, identity } = useIdpSession();

  const [group, setGroup] = useState<GroupDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [allIdentities, setAllIdentities] = useState<IdentitySummary[]>([]);
  const [selectedIdentity, setSelectedIdentity] = useState<IdentitySummary | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [removingId, setRemovingId] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionLoading && !isAuthenticated) {
      navigate('/auth/signin', { replace: true });
    }
  }, [isAuthenticated, sessionLoading, navigate]);

  const loadGroup = useCallback(async () => {
    if (groupId === undefined || groupId === '') return;
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const result = await adminApi.getGroup(groupId);
      setGroup(result);
    } catch (error) {
      if (error instanceof APIError) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(t('groupDetail.errors.loadGroup'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [groupId, t]);

  useEffect(() => {
    void loadGroup();
  }, [loadGroup]);

  async function handleOpenAddDialog() {
    setAddError(null);
    setSelectedIdentity(null);
    setAddDialogOpen(true);
    try {
      const result = await adminApi.listIdentities();
      setAllIdentities(result.data);
    } catch {
      setAddError(t('groupDetail.errors.loadUsers'));
    }
  }

  async function handleAddMember() {
    if (groupId === undefined || groupId === '' || selectedIdentity === null) return;
    setIsAdding(true);
    setAddError(null);
    try {
      await adminApi.addMember(groupId, selectedIdentity.identityId);
      setAddDialogOpen(false);
      setSuccessMessage(t('groupDetail.addedSuccess', { name: selectedIdentity.name }));
      await loadGroup();
    } catch (error) {
      if (error instanceof APIError) {
        setAddError(error.message);
      } else {
        setAddError(t('groupDetail.errors.addMember'));
      }
    } finally {
      setIsAdding(false);
    }
  }

  async function handleRemoveMember(identityId: string, memberName: string) {
    if (groupId === undefined || groupId === '') return;
    setRemovingId(identityId);
    try {
      await adminApi.removeMember(groupId, identityId);
      setSuccessMessage(t('groupDetail.removedSuccess', { name: memberName }));
      await loadGroup();
    } catch (error) {
      if (error instanceof APIError) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(t('groupDetail.errors.removeMember'));
      }
    } finally {
      setRemovingId(null);
    }
  }

  const currentIdentityId = identity?.identityId;
  const isPlatformAdmins = group?.name === 'platform-admins';

  if (sessionLoading || isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!group) {
    return <Alert severity="error">{errorMessage ?? t('groupDetail.groupNotFound')}</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h5" component="h1" fontWeight={700}>
          {group.name}
        </Typography>
        {group.isSystem && <Chip label={t('groups.typeSystem')} color="primary" size="small" />}
      </Stack>

      {errorMessage !== null ? <Alert severity="error">{errorMessage}</Alert> : null}

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" component="h2">
          {t('groupDetail.membersHeading', { count: group.members.length })}
        </Typography>
        <Button variant="contained" onClick={() => void handleOpenAddDialog()}>
          {t('groupDetail.addMemberButton')}
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('groupDetail.tableHeaders.name')}</TableCell>
              <TableCell>{t('groupDetail.tableHeaders.email')}</TableCell>
              <TableCell>{t('groupDetail.tableHeaders.joined')}</TableCell>
              <TableCell align="right">{t('groupDetail.tableHeaders.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {group.members.map((member) => {
              const isSelf = member.identityId === currentIdentityId;
              const disableRemove = isPlatformAdmins && isSelf;

              return (
                <TableRow key={member.identityId}>
                  <TableCell>{member.name}</TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>{formatDate(member.joinedAt)}</TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={
                        disableRemove
                          ? t('groupDetail.tooltipRemoveSelf')
                          : t('groupDetail.tooltipRemove')
                      }
                    >
                      <Box component="span">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={disableRemove || removingId === member.identityId}
                          onClick={() => void handleRemoveMember(member.identityId, member.name)}
                        >
                          {removingId === member.identityId ? (
                            <CircularProgress size={16} />
                          ) : (
                            <PersonRemoveIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {group.members.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {t('groupDetail.emptyMessage')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('groupDetail.dialogTitle', { groupName: group.name })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {addError !== null ? <Alert severity="error">{addError}</Alert> : null}
            <Autocomplete
              options={allIdentities.filter(
                (i) => !group.members.some((m) => m.identityId === i.identityId)
              )}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={selectedIdentity}
              onChange={(_event, value) => {
                setSelectedIdentity(value);
              }}
              renderInput={(params) => {
                return (
                  <TextField
                    id={params.id}
                    disabled={params.disabled}
                    fullWidth={params.fullWidth}
                    InputProps={params.InputProps}
                    inputProps={params.inputProps}
                    label={t('groupDetail.selectUserLabel')}
                    autoFocus
                    size="small"
                  />
                );
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddDialogOpen(false);
            }}
          >
            {t('groupDetail.cancelButton')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleAddMember()}
            disabled={!selectedIdentity || isAdding}
          >
            {isAdding ? t('groupDetail.addingButton') : t('groupDetail.addButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={() => {
          setSuccessMessage(null);
        }}
        message={successMessage}
      />
    </Stack>
  );
}
